import { useState, useEffect } from "react";
import { cloneDeep } from "lodash";

import CustomButtonLayout from "../layout/CustomButtonLayout";
import FormCustomDialog from "../dialogTypes/FormCustomDialog";
import { useTranslation } from "react-i18next";

/**
 * @COMPONENT_NAME CustomRecordButton
 * @DESCRIPTION 그리드에서 선택한 데이터를 기반으로 커스텀 다이얼로그를 열어 수정/생성할 수 있는 버튼 컴포넌트입니다.
 *              버튼 클릭 시 다이얼로그가 열리며, 스키마와 초기 데이터를 기반으로 Form을 렌더링하고, 작업 완료 시 refresh 및 콜백 처리를 합니다.
 *
 * @PROPS
 *    - {string} id - 컴포넌트 식별자
 *    - {object} schema - 렌더링할 Form 스키마
 *    - {object} grid - 데이터 그리드 객체 (선택된 항목 정보 활용)
 *    - {object} refresh - 데이터 갱신을 위한 객체, `refresh.refresh()` 호출
 *    - {Function} onSuccess - 작업 성공 시 호출되는 콜백
 *    - {string} mode - Form 모드 ("edit" | "create")
 *    - {string} title - 다이얼로그의 제목
 *    - {string} buttonName - 버튼 이름에 사용할 다국어 키
 *    - {boolean} isFormData - 초기 데이터를 formData로 넘길지 여부
 *    - {Function} modifyUrlCallback - 요청 URL을 커스터마이징하는 콜백
 *    - {Function} modifyFormDataCallback - Form 제출 전에 데이터를 수정하는 콜백
 *    - {Function} modifySchemaCallback - 스키마를 동적으로 수정하는 콜백
 *    - {Function} checkFormValidationCallback - 유효성 검사를 위한 콜백
 *    - {Function} customizeErrorMsgCallback - 에러 메시지를 커스터마이징하는 콜백
 *
 * @PASSTHROUGH_PROPS
 *    - {Function} modifyUrlCallback - 요청 URL을 커스터마이징하는 콜백
 *    - {Function} modifyFormDataCallback - Form 제출 전에 데이터를 수정하는 콜백
 *    - {Function} checkFormValidationCallback - 유효성 검사를 위한 콜백
 *    - {Function} customizeErrorMsgCallback - 에러 메시지를 커스터마이징하는 콜백
 *    - 
 *
 * @CALLBACKS
 *    - {Function} onUpdateSuccess - Form 제출 성공 시 호출되며, 상위에 알림 등을 전달할 수 있음
 *    - {Function} onClose - open state를 false로 변경
 *    - {Function} onOpen - open state를 true로 변경
 *
 * @EXAMPLES
 * ```jsx
 * <CustomRecordButton
 *    id="User"
 *    schema={userSchema}
 *    grid={userGrid}
 *    refresh={userApi}
 *    onSuccess={(msg) => notify(msg)}
 *    mode="edit"
 *    title="editUser"
 *    buttonName="edit"
 *    modifyUrlCallback={() => "/api/User/custom"}
 * />
 * ```
 */

function CustomRecordButton(props) {

    const {
        id,
        schema,
        grid,
        refresh,
        onSuccess,
        mode,
        method,
        title,
        buttonName,
        buttonFullName,

        isFormData,

        initDataCallBack,
        modifyUrlCallback,
        modifyFormDataCallback,
        modifySchemaCallback,
        checkFormValidationCallback,
        customizeErrorMsgCallback,
        modifyFormDataCallbackOK,
        checkDisabledCallback,
        modifyInitDataCallback,

        ...others
    } = props;

    const [open, setOpen] = useState(false);
    const [formData, setFormData] = useState();

    const [modifiedSchema, setModifiedSchema] = useState(schema);

    const { t } = useTranslation();

    useEffect(() => {
        setModifiedSchema(schema);

        if (modifySchemaCallback) {
            const newSchema = cloneDeep(schema);

            if (newSchema) {
                modifySchemaCallback(newSchema, { ...grid.selectedItems[0] });

                setModifiedSchema(newSchema);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schema]);

    const onOpen = () => {
        let initData = {};

        if (grid) {

            initData = { ...grid.selectedItems[0] }

        }

        if(initDataCallBack){
            initDataCallBack(initData)
        }

        if(modifyInitDataCallback){
            initData = modifyInitDataCallback(initData);
        }
        
        setFormData({...initData});
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };

    const onUpdateSuccess = (msg) => {
        if (onSuccess) {
            onSuccess(msg);
        }

        if (refresh) {
            refresh.refresh();
        }
    };

    return (
        <>
            <CustomButtonLayout  id={id + "custom-button"} onClick={onOpen} buttonName={buttonFullName ? buttonFullName : `buttonName.${modifiedSchema?.name}.${buttonName}`} {...others}  />
            <FormCustomDialog
                id={id + "custom-dialog"}
                mode={mode ? mode : "edit" }
                method={method}
                formId={id + "custom-form"}
                title={title ? t(`dialog.title.${modifiedSchema?.name}.${title}`) : "" }
                schema={modifiedSchema}
                initialData={isFormData ? formData : undefined}
                open={open}
                onClose={onClose}
                onSuccess={onUpdateSuccess}
                modifyUrlCallback={modifyUrlCallback}
                modifyFormDataCallback={modifyFormDataCallback}
                checkFormValidationCallback={checkFormValidationCallback}
                customizeErrorMsgCallback={customizeErrorMsgCallback}
                modifyFormDataCallbackOK={modifyFormDataCallbackOK}
                checkDisabledCallback={checkDisabledCallback}
            />
        </>
    );
}

export default CustomRecordButton;
